import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  UserPlus,
  CalendarClock,
  Receipt,
  PillBottle,
  LineChart,
  Settings,
  LogOut,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUiStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { BrandLockup } from '@/components/common/BrandMark'
import { getInitials } from '@/lib/utils'
import { FEATURES } from '@/lib/features'

const ALL_NAV_ITEMS = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true, feature: 'core' },
  { to: '/patients', label: 'Patients', icon: Users, feature: 'core' },
  { to: '/patients/new', label: 'Add Patient', icon: UserPlus, feature: 'core' },
  { to: '/drugs', label: 'Drug List', icon: PillBottle, feature: 'pharmacy' },
  { to: '/billing', label: 'Billing', icon: Receipt, feature: 'billing' },
  // { to: '/appointments', label: 'Appointments', icon: CalendarClock, soon: true, feature: 'core' },
  // { to: '/reports', label: 'Reports', icon: LineChart, soon: true, feature: 'core' },
  { to: '/settings', label: 'Settings', icon: Settings, feature: 'settings' },
]

// Filter to only items whose feature is enabled. `core` items always show.
const NAV_ITEMS = ALL_NAV_ITEMS.filter(
  (item) => item.feature === 'core' || FEATURES[item.feature],
)

export function Sidebar() {
  const { sidebarOpen, setSidebarOpen } = useUiStore()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/40 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-72 flex-col bg-white shadow-card ring-1 ring-ink-100 transition-transform duration-300 ease-out lg:translate-x-0 lg:shadow-none lg:ring-0 lg:border-r lg:border-ink-100',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <BrandLockup />
          <button
            onClick={() => setSidebarOpen(false)}
            className="rounded-lg p-1.5 text-ink-500 hover:bg-ink-100 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          <p className="px-3 pt-2 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-400">
            Workspace
          </p>
          <ul className="space-y-0.5">
            {NAV_ITEMS.map(({ to, label, icon: Icon, end, soon }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  end={end}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-150',
                      isActive
                        ? 'bg-primary-700 text-cream shadow-soft'
                        : 'text-ink-700 hover:bg-ink-50 hover:text-ink-900',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={cn(
                          'h-[18px] w-[18px] shrink-0 transition-transform group-hover:scale-110',
                          isActive ? 'text-cream' : 'text-ink-500 group-hover:text-primary-700',
                        )}
                        strokeWidth={2.2}
                      />
                      <span className="flex-1 truncate">{label}</span>
                      {soon && (
                        <span
                          className={cn(
                            'rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ring-1 ring-inset',
                            isActive
                              ? 'bg-cream/15 text-cream/80 ring-cream/30'
                              : 'bg-accent-50 text-accent-700 ring-accent-200',
                          )}
                        >
                          Soon
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User footer */}
        <div className="border-t border-ink-100 px-4 py-4">
          <div className="flex items-center gap-3 rounded-xl bg-ink-50/60 p-2.5">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.name}
                className="h-9 w-9 rounded-lg object-cover ring-1 ring-ink-200"
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-700 text-xs font-bold text-cream">
                {getInitials(user?.name || 'Dr')}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink-900">
                {user?.name || 'Doctor'}
              </p>
              <p className="truncate text-xs text-ink-500">
                {user?.email || 'staff@radiance.clinic'}
              </p>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="rounded-lg p-2 text-ink-500 transition hover:bg-white hover:text-rose-600"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
